import React, { useState } from 'react';
import { useLiveConfig } from '../data/firestore';
import { roleSatisfies } from '../data/entities';
import { displayedOrgPlan } from '../data/displayedPlan';
import { apiPost, isBackendConfigured } from '../utils/backendApi';
import { OrgAgree } from './Clickwrap';
import { legalStatus } from '../data/legal';

// Billing tab: current plan + entitlements from the org doc (written ONLY by
// the billing webhook), plan catalog from live config (billing.plans), and
// checkout/portal via the backend BillingProvider endpoints.
export default function OrgBilling({ org, myRole }) {
  const { doc: cfg } = useLiveConfig();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [agree, setAgree] = useState(null); // { key, docs } when checkout needs the org clickwrap
  const [code, setCode] = useState('');
  const [redeemed, setRedeemed] = useState(null);
  const isAdmin = roleSatisfies(myRole, 'admin');

  const sub = org.subscription || {};
  const plans = cfg?.billing?.plans || {};
  const shown = displayedOrgPlan(org, {
    plans: cfg ? plans : null,
    freeEntitlements: cfg?.billing?.freeEntitlements ?? null,
  });
  const subscribed = ['active', 'trialing', 'past_due'].includes(shown.status);
  const ents = org.entitlements || {};

  const subscribe = async (key) => {
    setBusy(key);
    setError('');
    try {
      const status = await legalStatus(org.id);
      if (!status.orgOk) {
        setAgree({ key, docs: status.agreements || [] });
        setBusy('');
        return;
      }
      const { url } = await apiPost('/billing/checkout', { orgId: org.id, planKey: key });
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  };

  const go = async (path, body, key) => {
    setBusy(key);
    setError('');
    try {
      const { url } = await apiPost(path, body);
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  };

  return (
    <div style={{ marginTop: 18, maxWidth: 640 }}>
      <div className="cs-card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <strong style={{ color: 'var(--text)' }}>{shown.name}</strong>
          {shown.status && (
            <span className="cs-help" style={{ fontSize: 12 }}>{shown.status}</span>
          )}
          <span style={{ flex: 1 }} />
          {subscribed && isAdmin && (
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              disabled={!!busy}
              onClick={() => go('/billing/portal', { orgId: org.id }, 'portal')}
            >
              {busy === 'portal' ? 'Opening…' : 'Manage billing'}
            </button>
          )}
        </div>
        {sub.currentPeriodEnd && subscribed && (
          <p className="cs-help" style={{ marginTop: 6 }}>
            {sub.source === 'referral' ? 'complimentary until' : 'renews'} {new Date(sub.currentPeriodEnd).toLocaleDateString()}
          </p>
        )}
        {sub.source === 'referral' && !sub.currentPeriodEnd && (
          <p className="cs-help" style={{ marginTop: 6 }}>complimentary (code {sub.code})</p>
        )}
        {Object.keys(ents).length > 0 && (
          <p className="cs-help" style={{ marginTop: 6 }}>
            {Object.entries(ents)
              .map(([k, v]) => `${k}: ${v === true ? 'yes' : v === false ? 'no' : v}`)
              .join(' · ')}
          </p>
        )}
      </div>

      {isAdmin && shown.selfServe && (
        <>
          <h3 style={{ marginTop: 22 }}>Plans</h3>
          {!isBackendConfigured() && (
            <p className="cs-help">Backend API not configured (REACT_APP_UPLOAD_API).</p>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            {Object.entries(plans).map(([key, plan]) => (
              <div key={key} className="cs-card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div>
                  <strong style={{ color: 'var(--text)' }}>{plan.name || key}{plan.priceLabel ? ` · ${plan.priceLabel}` : ''}</strong>
                  <p className="cs-help" style={{ margin: '4px 0 0' }}>
                    {Object.entries(plan.entitlements || {})
                      .map(([k, v]) => `${k}: ${v === true ? 'yes' : v === false ? 'no' : v}`)
                      .join(' · ')}
                  </p>
                </div>
                <span style={{ flex: 1 }} />
                {plan.priceId ? (
                  <button
                    className={`cs-btn cs-btn-sm ${sub.planKey === key && subscribed ? 'cs-btn-outline' : 'cs-btn-primary'}`}
                    disabled={!!busy || (sub.planKey === key && subscribed)}
                    onClick={() => subscribe(key)}
                  >
                    {sub.planKey === key && subscribed ? 'Current plan' : busy === key ? 'Redirecting…' : 'Subscribe'}
                  </button>
                ) : (
                  <span className="cs-help" style={{ fontSize: 12 }}>
                    price not configured (config/app billing.plans.{key}.priceId)
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {isAdmin && (
        <div className="cs-card" style={{ padding: 14, marginTop: 22 }} data-nova-note="Redeem a referral code: credits, a comped App Kit plan, or a comped Platform plan">
          <strong style={{ color: 'var(--text)' }}>Have a code?</strong>
          <p className="cs-help" style={{ margin: '4px 0 10px' }}>
            A referral code from nventr can add model credits, unlock App Kit without a card, or put this organization on a Platform plan.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!code.trim()) return;
              setBusy('code');
              setError('');
              setRedeemed(null);
              try {
                const out = await apiPost('/billing/referral', { orgId: org.id, code: code.trim() });
                if (!out.applied) { setError(out.reason || 'that code did not apply'); } else { setRedeemed(out); setCode(''); }
              } catch (err) {
                setError(err.message);
              } finally {
                setBusy('');
              }
            }}
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <input className="cs-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CODE" aria-label="Referral code" style={{ maxWidth: 220, textTransform: 'uppercase' }} />
            <button type="submit" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || !code.trim()}>{busy === 'code' ? 'Applying…' : 'Apply'}</button>
          </form>
          {redeemed && (
            <p className="cs-help" role="status" style={{ marginTop: 8, color: 'var(--ok)' }}>
              {redeemed.label} applied
              {redeemed.usd ? ` · $${redeemed.usd} model credits` : ''}
              {redeemed.kit ? ` · App Kit (${redeemed.kit}) with no checkout` : ''}
              {redeemed.platform ? ` · Platform ${redeemed.platform}` : ''}
              {redeemed.platformSkipped ? ` · Platform grant skipped (${redeemed.platformSkipped})` : ''}
              {redeemed.until ? ` · until ${new Date(redeemed.until).toLocaleDateString()}` : ''}
              . {redeemed.platform ? 'Reload to see the plan.' : ''}
            </p>
          )}
        </div>
      )}
      {agree && (
        <div style={{ marginTop: 16 }}>
          <OrgAgree
            orgId={org.id}
            docs={agree.docs}
            onDone={() => subscribe(agree.key)}
          />
        </div>
      )}
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 10 }}>{error}</p>}
    </div>
  );
}
